import type { SocialApplyStatus } from "@bbj-types/socialApply/enum";
import type { ObjectId } from "@bbj-types/utils";
import { useSuspense } from "@root/api/tools/axios";
import { useCallback } from "react";
import { axios, axiosInstance } from "../axios";

interface IUseGetSocialApplyByStatusParams {
  id: string;
  status:
    | SocialApplyStatus.PRESCRIPTION_FILLED
    | SocialApplyStatus.INTEGRATED_CANDIDATE
    | SocialApplyStatus.REFUSED_STRUCTURE;
}

export interface IUseGetSocialApplyByStatusQuery {
  filters: {
    search?: string | undefined;
    candidatePlace?: string[] | undefined;
    candidateStatus?: string[] | undefined;
  };
  skip: number;
  limit: number;
}

export interface IUseGetSocialOfferIntegrationResponse {
  _id: ObjectId;
  candidate: {
    _id: ObjectId;
    firstName: string;
    lastName: string;
  };
  author: {
    phone: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  firstReceptionDate?: Date | undefined;
  assessmentDate?: Date | undefined;
  status: SocialApplyStatus.INTEGRATED_CANDIDATE;
  prescriptionDate: Date;
  responseDate: Date;
  prescription: {
    documentId: string;
    name: string;
  };
  accompaniement: string;
  otherIntegratedSocialOffers: {
    name: string;
    integrationDate: Date;
  }[];
}

export interface IUseGetSocialOfferPrescriptionFilledResponse {
  _id: ObjectId;
  candidate: {
    _id: ObjectId;
    firstName: string;
    lastName: string;
    phone: string;
    place: string;
    status: string;
  };
  author: {
    phone: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  accompaniement: string;
  otherIntegratedSocialOffers: {
    name: string;
    integrationDate: Date;
  }[];
  status: SocialApplyStatus.PRESCRIPTION_FILLED;
  prescriptionDate: Date;
  prescription?: {
    documentId: string;
    name: string;
  };
}

export interface IUseGetSocialOfferRefusedCandidatesResponse {
  _id: ObjectId;
  candidate: {
    _id: ObjectId;
    firstName: string;
    lastName: string;
  };
  author: {
    phone: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  prescription: {
    documentId: string;
    name: string;
  };
  cause: string;
  status: SocialApplyStatus.REFUSED_STRUCTURE;
  assessmentDate: Date;
  responseDate: Date;
}

export type TUseGetSocialApplyByStatusResponse<T extends SocialApplyStatus> = {
  skip: number;
  limit: number;
  data: T extends SocialApplyStatus.INTEGRATED_CANDIDATE
    ? IUseGetSocialOfferIntegrationResponse[]
    : T extends SocialApplyStatus.PRESCRIPTION_FILLED
      ? IUseGetSocialOfferPrescriptionFilledResponse[]
      : T extends SocialApplyStatus.REFUSED_STRUCTURE
        ? IUseGetSocialOfferRefusedCandidatesResponse[]
        : never;
  total: number;
  count: number;
};

export const useGetSocialApplyByStatus = () => {
  const [state, execute, cancel] = axios<
    TUseGetSocialApplyByStatusResponse<SocialApplyStatus>,
    IUseGetSocialApplyByStatusParams,
    IUseGetSocialApplyByStatusQuery
  >(
    {
      method: "get",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (
      id: IUseGetSocialApplyByStatusParams["id"],
      status: IUseGetSocialApplyByStatusParams["status"],
      query: IUseGetSocialApplyByStatusQuery
    ) => {
      return execute({
        url: `/v2/offers/social/${id}/apply/${status}`,
        params: query,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetSocialApplyByStatusSuspense = (
  id: IUseGetSocialApplyByStatusParams["id"],
  status: IUseGetSocialApplyByStatusParams["status"],
  query: IUseGetSocialApplyByStatusQuery
) => {
  return useSuspense(
    () =>
      axiosInstance.get<TUseGetSocialApplyByStatusResponse<SocialApplyStatus>>(
        `/v2/offers/social/${id}/apply/${status}`,
        { params: query }
      ),
    [id, status, query]
  );
};
